'use client'

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { FaTimes, FaBars, FaChevronDown, FaFacebook, FaInstagram, FaYoutube } from 'react-icons/fa';
import { usePathname } from 'next/navigation';

type MainMenuProps = {
	scrollIndicatorEnabled?: boolean;
};

const logoVariants = ['/logo/logo.png', '/logo/logo.jpg'];

const MainMenu: React.FC<MainMenuProps> = ({ }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [code, setCode] = useState("");
	const pathname = usePathname();
	const [open, setOpen] = useState(false);
	const [activeLogoIndex, setActiveLogoIndex] = useState(0);

	const router = useRouter();
	const menuRef = useRef<HTMLDivElement>(null);

	const handleMenuToggle = () => {
		setIsOpen((prev) => !prev);
	};

	const handleJoinClick = () => {
		if (code.trim()) {
			router.push(`/check/${code}`);
			setCode("");
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: any) => {
			if (menuRef.current && !menuRef.current.contains(event.target)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	useEffect(() => {
		const intervalId = window.setInterval(() => {
			setActiveLogoIndex((prevIndex) => (prevIndex + 1) % logoVariants.length);
		}, 3000);

		return () => window.clearInterval(intervalId);
	}, []);

	const isChildPage = pathname?.startsWith('/about') && pathname !== '/about';

	const handleLinkClick = () => {
		setIsOpen(false)
	}


	return (
		<div className='bg-white max-w-[1920px] mx-auto'>
			<header className="h-[160px] top-0 left-0 w-full bg-white z-40 border-b border-gray-200 fixed md:relative max-w-6xl 2xl:max-w-7xl mx-auto py-4 lg:py-0">
				<div className="z-40 flex items-center justify-between px-4 space-x-4 mx-auto h-full" >
					<div className='h-full flex flex-row xl:space-x-10  space-x-5 items-center'>
						<Link href="/" className="block">
							<div className="relative h-[130px] w-[160px] overflow-hidden">
								{logoVariants.map((logoSrc, index) => {
									const isActive = index === activeLogoIndex;
									const isJpg = logoSrc.endsWith(".jpg");

									return (
										<div
											key={logoSrc}
											className={`absolute inset-0 flex justify-center transition-all duration-700 ease-in-out ${isJpg ? "items-end pb-[25px]" : "items-end"
												} ${isActive
													? "translate-x-0 opacity-100"
													: "-translate-x-4 opacity-0"
												}`}
										>
											<Image
												src={logoSrc}
												alt="Logo"
												width={160}
												height={48}
												priority={index === 0}
												className="object-contain"
											/>
										</div>
									);
								})}
							</div>
						</Link>

						<div className="hidden lg:flex items-center xl:space-x-10  space-x-5 flex-1 justify-center relative mt-15">
							<div>
								<button
									onClick={() => setOpen(!open)}
									className={`uppercase font-semibold transition text-sm ${isChildPage ? 'text-sun-500' : 'text-ash-500 hover:text-sun-500'}`}
								>
									Бидний тухай
									<FaChevronDown className='inline-block ms-1' />
								</button>
								{open && (
									<div ref={menuRef} className="absolute top-full mt-2 ms-10 w-60 p-4 bg-ash-500/70 shadow-lg rounded-md z-50">
										<Link href="/about" className="block px-4 py-2 text-sm hover:text-sun-500 text-white uppercase" onClick={() => setOpen(false)}>
											Бид хэн бэ?
										</Link>
										<Link href="/about/team" className="block px-4 py-2 text-sm hover:text-sun-500 text-white uppercase" onClick={() => setOpen(false)}>
											Бидний хамт олон
										</Link>
										<Link href="/about/join" className="block px-4 py-2 text-sm hover:text-sun-500 text-white uppercase" onClick={() => setOpen(false)}>
											Бидэнтэй нэгдэх үү
										</Link>
										<Link href="/about/know" className="block px-4 py-2 text-sm hover:text-sun-500 text-white uppercase" onClick={() => setOpen(false)}>
											Мэдсэн үү
										</Link>
										<Link href="/about/contact" className="block px-4 py-2 text-sm hover:text-sun-500 text-white uppercase" onClick={() => setOpen(false)}>
											Бидэнтэй холбогдох
										</Link>
									</div>
								)}
							</div>

							<Link href="/#register">
								<button className={`uppercase font-semibold transition text-sm ${pathname === '/register' ? 'text-sun-500' : 'text-ash-500 hover:text-sun-500'}`}>
									Зусланд амрах уу?
								</button>
							</Link>

							<Link href="/#clubs">
								<button className={`uppercase font-semibold transition text-sm ${pathname === '/clubs' ? 'text-sun-500' : 'text-ash-500 hover:text-sun-500'}`}>
									Клубүүд
								</button>
							</Link>

							<Link href="/#partners">
								<button className={`uppercase font-semibold transition text-sm ${pathname === '/partners' ? 'text-sun-500' : 'text-ash-500 hover:text-sun-500'}`}>
									Хамтрагч байгууллагууд
								</button>
							</Link>

							<div className='flex flex-row space-x-2 text-ash-500'>
								<a href="https://www.facebook.com/narssummercamp?mibextid=LQQJ4d" target="_blank" rel="noopener noreferrer" className="text-2xl hover:text-blue-500">
									<FaFacebook />
								</a>
								<a href="https://www.instagram.com/narscamp.mongolia/" target="_blank" rel="noopener noreferrer" className="text-2xl hover:text-pink-500">
									<FaInstagram />
								</a>
								<a href="https://www.youtube.com/@narschildrenscamp9739" target="_blank" rel="noopener noreferrer" className="text-2xl hover:text-red-500">
									<FaYoutube />
								</a>
							</div>
						</div>
					</div>

					<div className='flex items-center justify-end lg:mt-15'>
						<input
							type="text"
							placeholder="Бүртгэлийн код"
							name="name"
							className="text-sm bg-white border-gray-300 border border-r-0 py-3 rounded-l text-center focus:border-r-0 focus:outline-blue-500 w-32"
							onChange={(e) => setCode(e.target.value)}
							value={code}
						/>
						<button
							onClick={handleJoinClick}
							className="bg-gradient-to-br from-ash-500 to-gray-600  text-white font-semibold px-4 py-[13px] rounded-r transition text-sm cursor-pointer">
							Шалгах
						</button>
					</div>

					<div className="items-center space-x-4 p-2 lg:hidden flex">
						<button onClick={handleMenuToggle} aria-label="Menu" className="py-3 text-ash-500">
							{isOpen ? <FaTimes style={{ fontSize: 25 }} /> : <FaBars style={{ fontSize: 25 }} />}
						</button>
					</div>
				</div>
			</header>

			<div
				className={`lg:hidden fixed z-30 inset-0 bg-white transition-transform transform ${isOpen ? 'translate-y-0' : '-translate-y-full'}`}
			>
				<div className="flex flex-col items-center p-6 space-y-6 h-full overflow-y-auto mt-[180px]">
					<div className="">
						<button
							className="text-xl font-semibold flex items-center justify-between w-full text-ash-500"
						>
							Бидний тухай
							<FaChevronDown className={`ml-2 transition-transform ${open ? 'rotate-180' : ''}`} />
						</button>
						<div className="pl-4 pt-2 space-y-2">
							<a href="/about">
								<div className="text-lg cursor-pointer text-ash-500">Бид хэн бэ?</div>
							</a>
							<a href="/about/team">
								<div className="text-lg cursor-pointer text-ash-500">Бидний хамт олон</div>
							</a>
							<a href="/about/join">
								<div className="text-lg cursor-pointer text-ash-500">Бидэнтэй нэгдэх үү</div>
							</a>
							<a href="/about/know">
								<div className="text-lg cursor-pointer text-ash-500">Мэдсэн үү</div>
							</a>
							<a href="/about/contact">
								<div className="text-lg cursor-pointer text-ash-500">Бидэнтэй холбогдох</div>
							</a>
						</div>
					</div>

					<Link onClick={handleLinkClick} href="/#register"><div className="text-xl font-semibold cursor-pointer text-ash-500">Зусланд амрах уу?</div></Link>
					<Link onClick={handleLinkClick} href="/#clubs"><div className="text-xl font-semibold cursor-pointer text-ash-500">Клубүүд</div></Link>
					<Link onClick={handleLinkClick} href="/#partners"><div className="text-xl font-semibold cursor-pointer text-ash-500">Хамтрагч</div></Link>

					{/* Social links */}
					<div className="flex flex-row space-x-4 pt-4  text-ash-500">
						<a href="https://www.facebook.com/narssummercamp?mibextid=LQQJ4d" target="_blank" rel="noopener noreferrer">
							<FaFacebook className="text-2xl hover:text-blue-500" />
						</a>
						<a href="https://www.instagram.com/narscamp.mongolia/" target="_blank" rel="noopener noreferrer">
							<FaInstagram className="text-2xl hover:text-pink-500" />
						</a>
						<a href="https://www.youtube.com/@narschildrenscamp9739" target="_blank" rel="noopener noreferrer">
							<FaYoutube className="text-2xl hover:text-red-500" />
						</a>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MainMenu;